import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Collapsible,
  DataList,
  Field,
  HStack,
  Heading,
  Icon,
  Input,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { ChevronDown, Plug } from 'lucide-react';
import RelativeTime from '@/components/ui/RelativeTime';
import {
  useGetClarityConnectionQuery,
  useLookupClarityOrganizationMutation,
  useConnectClarityMutation,
  useDisconnectClarityMutation,
  useUpdateClarityConnectionMutation,
} from './api';
import {
  type ClarityConnection,
  type ClarityErrorDescription,
  type ClarityLookup,
  GENERIC_CLARITY_ERROR,
  MAX_CLARITY_ID_LENGTH,
  clarityRejectionHint,
  describeClarityError,
  normalizeClarityOrganizationId,
  normalizeOrigins,
} from './types';
import { useClarityAccess } from './use-clarity-access';

interface ClarityConnectionSectionProps {
  organizationId: number;
  isOpen: boolean;
}

/**
 * SuperGod-only "Clarity integration" section of the organization drawer.
 * Renders nothing (and sends nothing) unless VITE_CLARITY_ENABLED is 'true' and the
 * current user is a SuperGod. Collapsed by default; the data session (GET, form,
 * lookup confirmation) only exists while expanded.
 */
export function ClarityConnectionSection({ organizationId, isOpen }: Readonly<ClarityConnectionSectionProps>) {
  const access = useClarityAccess();
  if (!access || !isOpen) return null;

  return (
    <Box
      key={organizationId}
      p={4}
      borderWidth={1}
      borderRadius="md"
      borderColor="gray.200"
      bg="white"
      _dark={{ borderColor: 'gray.600', bg: 'gray.700' }}
      data-testid="clarity-connection-section"
    >
      <Collapsible.Root defaultOpen={false} lazyMount unmountOnExit>
        <Collapsible.Trigger asChild>
          <Button variant="ghost" w="full" justifyContent="space-between" px={0} _hover={{ bg: 'transparent' }}>
            <HStack>
              <Icon as={Plug} boxSize={4} color="brand.500" />
              <Heading size="sm" color="brand.600" _dark={{ color: 'brand.300' }}>
                Clarity integration
              </Heading>
            </HStack>
            <Collapsible.Indicator transition="transform 0.2s" _open={{ transform: 'rotate(180deg)' }}>
              <Icon as={ChevronDown} boxSize={4} />
            </Collapsible.Indicator>
          </Button>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <ConnectionSession organizationId={organizationId} />
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}

const sameOrigins = (a: string[], b: string[]) => a.length === b.length && a.every((origin, i) => origin === b[i]);

type ProvisioningAction =
  | { kind: 'check' | 'connect'; clarityOrganizationId: string }
  | { kind: 'disconnect' };

function ConnectionSession({ organizationId }: Readonly<{ organizationId: number }>) {
  const access = useClarityAccess();
  const { currentData: connection, error: loadError, isError, isFetching, refetch } = useGetClarityConnectionQuery(organizationId, {
    skip: !access,
  });
  const [update, updateState] = useUpdateClarityConnectionMutation();
  const [lookup, lookupState] = useLookupClarityOrganizationMutation();
  const [connect, connectState] = useConnectClarityMutation();
  const [disconnect, disconnectState] = useDisconnectClarityMutation();
  // Also guard calls in the same render, before mutation loading state reaches the UI.
  const busy = useRef(false);
  const [clarityId, setClarityId] = useState('');
  const [originsText, setOriginsText] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<ClarityErrorDescription>({});
  const [check, setCheck] = useState<{ id: string; result: ClarityLookup } | null>(null);
  const [lastAction, setLastAction] = useState<ProvisioningAction | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  useEffect(() => {
    if (connection && !initialized) {
      setClarityId(connection.clarity_organization_id ?? '');
      setOriginsText(connection.public_origins.join('\n'));
      setInitialized(true);
    }
  }, [connection, initialized]);

  if (!connection) {
    return isError && !isFetching ? <LoadFailure error={loadError} onReload={refetch} /> : <Text fontSize="sm" color="gray.500" pt={3}>Loading…</Text>;
  }

  const pending = updateState.isLoading || lookupState.isLoading || connectState.isLoading || disconnectState.isLoading;
  const dirty = !sameOrigins(normalizeOrigins(originsText).origins, connection.public_origins);
  const normalizedId = normalizeClarityOrganizationId(clarityId);
  const showConnect = !connection.enabled || connection.clarity_organization_id === null;
  const connectHint = dirty ? 'Save your changes before connecting.'
    : connection.public_origins.length === 0 ? 'Save at least one public origin before connecting.'
    : 'error' in normalizedId ? normalizedId.error
    : check?.id !== normalizedId.value ? 'Check the Clarity organization ID before connecting.'
    : check.result.connected && !check.result.connected_to_this_organization ? 'This Clarity organization is connected to another WellPrept organization.'
    : null;

  const showError = (error: unknown) => {
    const description = describeClarityError(error);
    setFieldErrors(description.fields ?? {});
    setFeedback(description.fields ? {} : description);
  };

  const clearFeedback = () => {
    setFeedback({});
    setFieldErrors({});
    setLastAction(null);
  };

  const onSave = async () => {
    if (busy.current || pending) return;
    clearFeedback();
    const origins = normalizeOrigins(originsText);
    // Keep server public_origins.N errors aligned with the visible normalized lines.
    setOriginsText(origins.origins.join('\n'));
    const errors = Object.fromEntries(Object.entries(origins.errors).map(([index, message]) =>
      [`public_origins.${index}`, `Line ${Number(index) + 1}: ${message}`]));
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    busy.current = true;
    try {
      const saved = await update({
        organizationId,
        body: { enabled: connection.enabled, public_origins: origins.origins },
      }).unwrap();
      setOriginsText(saved.public_origins.join('\n'));
    } catch (error) {
      showError(error);
    } finally {
      busy.current = false;
    }
  };

  const runAction = async (action: ProvisioningAction) => {
    if (busy.current || pending) return;
    if (action.kind === 'connect' && (connectHint || !showConnect)) return;
    if (action.kind === 'disconnect' && showConnect) return;
    if (action.kind !== 'disconnect') {
      const id = normalizeClarityOrganizationId(action.clarityOrganizationId);
      if ('error' in id) {
        clearFeedback();
        setFieldErrors({ clarity_organization_id: id.error });
        return;
      }
      if (id.value !== clarityId.trim()) return;
    }
    busy.current = true;
    clearFeedback();
    setLastAction(action);
    if (action.kind === 'check') setCheck(null);
    setConfirmDisconnect(false);
    try {
      if (action.kind === 'check') {
        const result = await lookup({ organizationId, clarityOrganizationId: action.clarityOrganizationId }).unwrap();
        setCheck({ id: action.clarityOrganizationId, result });
      } else {
        const saved = action.kind === 'connect'
          ? await connect({ organizationId, clarityOrganizationId: action.clarityOrganizationId }).unwrap()
          : await disconnect(organizationId).unwrap();
        setClarityId(saved.clarity_organization_id ?? '');
        setCheck(null);
      }
      setLastAction(null);
    } catch (error) {
      showError(error);
    } finally {
      busy.current = false;
    }
  };

  const idError = fieldErrors.clarity_organization_id;
  const originErrors = Object.entries(fieldErrors).filter(([key]) => key === 'public_origins' || key.startsWith('public_origins.'));
  const otherErrors = Object.entries(fieldErrors).filter(([key]) => key !== 'clarity_organization_id' && key !== 'public_origins' && !key.startsWith('public_origins.'));
  const provisioning = feedback.provisioning;
  const rejectionHint = provisioning?.kind === 'rejected' ? clarityRejectionHint(provisioning.code) : null;

  return (
    <VStack align="stretch" gap={4} pt={3}>
      <Field.Root invalid={originErrors.length > 0}>
        <Field.Label>Public origins (one https origin per line)</Field.Label>
        <Textarea
          size="sm"
          rows={3}
          value={originsText}
          onChange={(event) => { setOriginsText(event.target.value); clearFeedback(); }}
          disabled={pending}
          placeholder="https://www.example.org"
        />
        {originErrors.map(([key, message]) => <Field.ErrorText key={key}>{message}</Field.ErrorText>)}
      </Field.Root>
      <Button size="sm" colorPalette="brand" alignSelf="start" onClick={onSave} disabled={pending}>Save</Button>

      {showConnect ? (
        <>
          {connection.enabled && <Text fontSize="sm">Connected manually. Connect once with the Clarity organization ID to enable Disconnect.</Text>}
          <Field.Root invalid={Boolean(idError)}>
            <Field.Label>Clarity organization ID</Field.Label>
            <Input
              size="sm"
              value={clarityId}
              maxLength={MAX_CLARITY_ID_LENGTH}
              onChange={(event) => {
                setClarityId(event.target.value);
                setCheck(null);
                clearFeedback();
              }}
              disabled={pending}
            />
            <Field.HelperText>Paste the ID from Clarity, then check the organization name before connecting.</Field.HelperText>
            {idError && <Field.ErrorText>{idError}</Field.ErrorText>}
          </Field.Root>
          <HStack gap={2} flexWrap="wrap">
            <Button size="sm" variant="outline" onClick={() => runAction({ kind: 'check', clarityOrganizationId: clarityId.trim() })} disabled={pending}>Check</Button>
            <Button size="sm" colorPalette="brand" onClick={() => runAction({ kind: 'connect', clarityOrganizationId: clarityId.trim() })} disabled={pending || Boolean(connectHint)}>
              {check ? `Connect to ${check.result.name}` : 'Connect'}
            </Button>
          </HStack>
          {check && (
            <Box role="status">
              <Text fontWeight="medium">{check.result.name}</Text>
              <Text fontSize="sm">{!check.result.connected ? 'Not connected yet.'
                : check.result.connected_to_this_organization ? 'Already connected to this WellPrept organization. Connecting again issues a new catalog token.'
                : 'Connected to another WellPrept organization. Check the Clarity organization ID.'}</Text>
            </Box>
          )}
          {connectHint && <Text fontSize="xs" color="gray.500">{connectHint}</Text>}
        </>
      ) : (
        <>
          {confirmDisconnect ? (
            <VStack align="start" gap={2} p={3} borderWidth={1} borderRadius="md" borderColor="orange.300">
              <Text fontSize="sm">Clarity stops syncing this organization. Indexed content is kept. Widgets disappear after the next refresh.</Text>
              <HStack>
                <Button size="sm" colorPalette="orange" onClick={() => runAction({ kind: 'disconnect' })} disabled={pending}>Disconnect</Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDisconnect(false)} disabled={pending}>Cancel</Button>
              </HStack>
            </VStack>
          ) : (
            <Button size="sm" variant="outline" alignSelf="start" onClick={() => { clearFeedback(); setConfirmDisconnect(true); }} disabled={pending}>Disconnect</Button>
          )}
          {idError && <Text fontSize="sm" color="red.500" role="alert">{idError}</Text>}
        </>
      )}

      {otherErrors.map(([key, message]) => <Text key={key} fontSize="sm" color="red.500" role="alert">{message}</Text>)}
      {feedback.message && <Text fontSize="sm" color="red.500" role="alert">{feedback.message}</Text>}
      {provisioning && (
        <Box role="alert">
          <Text fontSize="sm" color="red.500">{provisioning.kind === 'rejected'
            ? `Clarity rejected the request (code: ${provisioning.code})`
            : `Clarity is unreachable (code: ${provisioning.code}). Try again.`}</Text>
          {rejectionHint && <Text fontSize="sm">{rejectionHint}</Text>}
          {provisioning.kind === 'unavailable' && lastAction && (
            <Button size="sm" variant="outline" mt={2} onClick={() => runAction(lastAction)} disabled={pending}>Try again</Button>
          )}
        </Box>
      )}
      <ConnectionStatus connection={connection} />
    </VStack>
  );
}

function LoadFailure({ error, onReload }: Readonly<{ error: unknown; onReload: () => void }>) {
  return (
    <VStack align="start" gap={2} pt={3}>
      <Text fontSize="sm" color="red.500" role="alert">{describeClarityError(error).message ?? GENERIC_CLARITY_ERROR}</Text>
      <Button size="sm" variant="outline" onClick={() => onReload()}>Reload</Button>
    </VStack>
  );
}

function ConnectionStatus({ connection }: Readonly<{ connection: ClarityConnection }>) {
  return (
    <DataList.Root orientation="horizontal" size="sm" data-testid="clarity-status">
      <StatusItem label="Connection">
        <Badge size="sm" colorPalette={connection.enabled ? 'green' : 'gray'} variant="subtle">
          {connection.enabled ? 'Connected' : 'Not connected'}
        </Badge>
      </StatusItem>
      <StatusItem label="Clarity organization ID">{connection.clarity_organization_id ?? 'Not set'}</StatusItem>
      <StatusItem label="External organization ID">{connection.external_organization_id ?? 'set on connect'}</StatusItem>
      <StatusItem label="Token">{connection.has_token ? 'Stored' : 'Missing'}</StatusItem>
      <StatusItem label="Previous token still valid">{connection.has_previous_token ? 'Yes' : 'No'}</StatusItem>
      <StatusItem label="Token rotated">
        {connection.token_rotated_at ? <RelativeTime date={connection.token_rotated_at} fontSize="sm" /> : 'Never'}
      </StatusItem>
      <StatusItem label="Last saved">
        {connection.updated_at ? <RelativeTime date={connection.updated_at} fontSize="sm" /> : 'Not saved yet'}
      </StatusItem>
      <StatusItem label="Widgets ready">{connection.widgets_ready_count}</StatusItem>
      <StatusItem label="Widgets not ready">{connection.widgets_not_ready_count}</StatusItem>
      <StatusItem label="Files awaiting preparation">{connection.unprepared_file_count}</StatusItem>
      <StatusItem label="Files that cannot be published">{connection.unresolvable_file_count}</StatusItem>
    </DataList.Root>
  );
}

function StatusItem({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <DataList.Item>
      <DataList.ItemLabel>{label}</DataList.ItemLabel>
      <DataList.ItemValue>{children}</DataList.ItemValue>
    </DataList.Item>
  );
}
